import {unique_id} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'
import type {Signal} from '@bitstillery/mithril'
import {$t} from '@bitstillery/common/app'
import {classes} from '@bitstillery/common/lib/utils'
import m from '@bitstillery/mithril'
import type {FilterI18nResolved} from '@bitstillery/common/types'

/** `[value, label]`; a filter's option adds its count and its child options. */
export type CheckboxOption = [value: string | number, label: string, count?: number, children?: CheckboxOption[]]

interface CheckboxGroupAttrs {
    filter_count?: boolean | undefined
    options: CheckboxOption[]
    model: Signal<(string | number)[]>
    disabled?: boolean
    translate?: FilterI18nResolved | undefined
}

export class CheckboxGroup extends MithrilComponent<CheckboxGroupAttrs> {
    name = unique_id()

    /**
     * Generic checkbox event handler that deals with
     * siblings, parents and children.
     * @param e - The original event
     * @param vnode - The Mithril Vnode
     * @param option - The current option datamodel
     * @param parent - A reference the current option's parent
     */
    on_change(
        e: Event & {currentTarget: HTMLInputElement},
        vnode: m.Vnode<CheckboxGroupAttrs>,
        option: CheckboxOption,
        parent: CheckboxOption | undefined,
    ) {
        const selection = [...vnode.attrs.model.value] // Modifying the original directly would trigger multiple redraws
        if (e.currentTarget.checked) {
            // Selecting current option
            if (!selection.includes(option[0])) {
                selection.push(option[0])
            }

            // Selected option has parent
            if (parent?.[3] && !selection.includes(parent[0])) {
                let all_sibling_selected = true
                for (const sibling_option of parent[3]) {
                    if (!selection.includes(sibling_option[0])) {
                        all_sibling_selected = false
                    }
                }
                if (all_sibling_selected) {
                    selection.push(parent[0]) // Select parent; all of the child's sibling options are selected
                }
            }

            if (option[3]) {
                // Selected option has children; select all of the unselected children
                for (const child_option of option[3]) {
                    if (!selection.includes(child_option[0])) {
                        selection.push(child_option[0])
                    }
                }
            }
        } else {
            // Deselecting current option
            if (selection.includes(option[0])) {
                selection.splice(selection.indexOf(option[0]), 1)
            }

            // Deselected option has parent
            if (Array.isArray(parent) && selection.includes(parent[0])) {
                selection.splice(selection.indexOf(parent[0]), 1) // Deselect parent; not all child options are selected anymore
            }

            // Deselected option has children; deselect all of them
            if (option[3]) {
                for (const child_option of option[3]) {
                    if (selection.includes(child_option[0])) {
                        selection.splice(selection.indexOf(child_option[0]), 1)
                    }
                }
            }
        }
        vnode.attrs.model.value.splice(0, vnode.attrs.model.value.length, ...selection)
    }

    render_options(
        vnode: m.Vnode<CheckboxGroupAttrs>,
        options: CheckboxOption[],
        depth: number,
        parent: CheckboxOption | undefined,
    ) {
        return options.map((option) => {
            const has_children = option.length === 4 && Array.isArray(option[3]) && option[3].length
            let disabled = false,
                selected = false
            if (option.length >= 3) {
                if (vnode.attrs.filter_count && option[2] === 0) {
                    disabled = true
                }
                if (vnode.attrs.model.value.includes(option[0])) {
                    selected = true
                }
            }

            return (
                <div
                    className={classes(`cb-wrapper-${depth}`, {
                        'has-children': has_children,
                        'no-children': !has_children,
                    })}
                >
                    <div className={classes('item', {disabled, selected})}>
                        <input
                            checked={vnode.attrs.model.value.includes(option[0])}
                            disabled={vnode.attrs.disabled}
                            id={`${this.name}-${option[0]}`}
                            name={`${this.name}-${option[0]}`}
                            onchange={(e) => this.on_change(e, vnode, option, parent)}
                            type='checkbox'
                        />
                        <label for={`${this.name}-${option[0]}`}>
                            {(() => {
                                if (vnode.attrs.translate) {
                                    const {prefix, label} = vnode.attrs.translate
                                    const key = label ? label(option[1]) : String(option[0])
                                    return $t(`${prefix}${key}`)
                                }
                                return option[1]
                            })()}
                        </label>
                        {vnode.attrs.filter_count && option.length >= 3 && <div className='count'>{option[2]}</div>}
                    </div>
                    {option.length === 4 && Array.isArray(option[3]) && (
                        <div class='sub-items'>{this.render_options(vnode, option[3], depth + 1, option)}</div>
                    )}
                </div>
            )
        })
    }

    view(vnode: m.Vnode<CheckboxGroupAttrs>) {
        return <div className='c-checkbox-group'>{this.render_options(vnode, vnode.attrs.options, 1, undefined)}</div>
    }
}
